import { z } from "zod";

export const MAX_VIDEO_BYTES = 4 * 1024 * 1024 * 1024; // 4 GiB safety ceiling
export const MAX_THUMBNAIL_BYTES = 10 * 1024 * 1024; // 10 MiB

export const ALLOWED_VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-matroska",
] as const;

export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

// For fields that render as an <img src>, not a navigable link — restricting
// to http(s) rules out `javascript:`/`data:`/other schemes up front, on top
// of whatever the browser itself already refuses to execute from an <img>.
export const httpUrlSchema = z
  .string()
  .trim()
  .max(500)
  .url("Must be a valid URL")
  .refine((v) => /^https?:\/\//i.test(v), "Must be an http:// or https:// URL");

export const createVideoSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).optional(),
});

export const updateVideoSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(5000).nullable().optional(),
});

export const uploadUrlRequestSchema = z.object({
  contentType: z.enum(ALLOWED_VIDEO_MIME_TYPES),
  fileSizeBytes: z
    .number()
    .int()
    .positive()
    .max(MAX_VIDEO_BYTES),
});

export const thumbnailUploadUrlRequestSchema = z.object({
  contentType: z.enum(ALLOWED_IMAGE_MIME_TYPES),
  fileSizeBytes: z
    .number()
    .int()
    .positive()
    .max(MAX_THUMBNAIL_BYTES),
});

export const finalizeUploadSchema = z.object({
  kind: z.enum(["video", "thumbnail"]),
  key: z.string().min(1),
  durationSeconds: z.number().positive().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

export const youtubeImportSchema = z.object({
  url: z
    .string()
    .url()
    .refine(
      (value) =>
        /^https?:\/\/(www\.|m\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/.test(
          value,
        ),
      { message: "Must be a youtube.com or youtu.be video URL" },
    ),
  title: z.string().trim().min(1).max(200).optional(),
});

export const jobCallbackSchema = z.object({
  status: z.enum(["queued", "running", "succeeded", "failed"]),
  progressPct: z.number().int().min(0).max(100).optional(),
  errorMessage: z.string().max(2000).optional(),
  result: z
    .object({
      r2Key: z.string(),
      r2ThumbnailKey: z.string().optional(),
      fileSizeBytes: z.number().int().positive(),
      durationSeconds: z.number().positive().optional(),
      width: z.number().int().positive().optional(),
      height: z.number().int().positive().optional(),
      mimeType: z.string().optional(),
      title: z.string().optional(),
    })
    .optional(),
});

const USERNAME_REGEX = /^[A-Za-z0-9_-]{3,32}$/;

export const createCreatorSchema = z.object({
  username: z.string().regex(USERNAME_REGEX, "3-32 letters, digits, _ or -"),
  displayName: z.string().trim().min(1).max(60),
  email: z.string().trim().email(),
  bio: z.string().trim().max(2000).optional(),
});

/** Admin-only edits — deliberately excludes isOfficial (see /designate-official). */
export const adminUpdateCreatorSchema = z.object({
  username: z.string().regex(USERNAME_REGEX).optional(),
  displayName: z.string().trim().min(1).max(60).optional(),
  email: z.string().trim().email().optional(),
  bio: z.string().trim().max(2000).nullable().optional(),
  avatarUrl: httpUrlSchema.nullable().optional(),
  status: z.enum(["pending", "approved", "revoked"]).optional(),
  canUpload: z.boolean().optional(),
  isVerified: z.boolean().optional(),
});

/** What a creator may change on their own profile — no status/permission fields. */
export const selfUpdateCreatorSchema = z.object({
  displayName: z.string().trim().min(1).max(60).optional(),
  bio: z.string().trim().max(2000).nullable().optional(),
  avatarUrl: httpUrlSchema.nullable().optional(),
});

// --- Accounts, login, applications --------------------------------------

// NIST 800-63B favors length over forced complexity rules — no
// upper/lower/digit/symbol requirement, just a meaningful minimum length
// and a cap to bound processing cost on absurdly long input.
export const passwordSchema = z.string().min(10, "At least 10 characters").max(256);

export const loginSchema = z.object({
  email: z.string().trim().email(),
  password: z.string().min(1).max(256),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: passwordSchema,
});

export const applyCreatorSchema = z.object({
  requestedUsername: z.string().regex(USERNAME_REGEX, "3-32 letters, digits, _ or -"),
  displayName: z.string().trim().min(1).max(60),
  email: z.string().trim().email(),
  youtubeUrl: z.string().trim().url().max(300).optional(),
  minecraftUsername: z.string().trim().regex(/^[A-Za-z0-9_]{2,16}$/).optional(),
  discordHandle: z.string().trim().max(60).optional(),
  reason: z.string().trim().min(20, "Please share a bit more detail").max(3000),
  otherLinks: z.string().trim().max(2000).optional(),
  additionalInfo: z.string().trim().max(3000).optional(),
});

export const reviewApplicationSchema = z.object({
  status: z.enum(["pending", "needs_info", "approved", "rejected"]),
  reviewNotes: z.string().trim().max(3000).nullable().optional(),
});

/** Lets the admin adjust the requested identity before activating an approved application. */
export const activateApplicationSchema = z.object({
  username: z.string().regex(USERNAME_REGEX).optional(),
  displayName: z.string().trim().min(1).max(60).optional(),
  email: z.string().trim().email().optional(),
});

export type CreateVideoInput = z.infer<typeof createVideoSchema>;
export type UpdateVideoInput = z.infer<typeof updateVideoSchema>;
export type UploadUrlRequest = z.infer<typeof uploadUrlRequestSchema>;
export type ThumbnailUploadUrlRequest = z.infer<
  typeof thumbnailUploadUrlRequestSchema
>;
export type FinalizeUploadInput = z.infer<typeof finalizeUploadSchema>;
export type YoutubeImportInput = z.infer<typeof youtubeImportSchema>;
export type JobCallbackInput = z.infer<typeof jobCallbackSchema>;
export type CreateCreatorInput = z.infer<typeof createCreatorSchema>;
export type AdminUpdateCreatorInput = z.infer<typeof adminUpdateCreatorSchema>;
export type SelfUpdateCreatorInput = z.infer<typeof selfUpdateCreatorSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
export type ApplyCreatorInput = z.infer<typeof applyCreatorSchema>;
export type ReviewApplicationInput = z.infer<typeof reviewApplicationSchema>;
export type ActivateApplicationInput = z.infer<typeof activateApplicationSchema>;
